// Shared helpers for voice call UI
export const TERMINAL_STATUSES = [
  'completed',
  'busy',
  'failed',
  'no-answer',
  'canceled',
];

// Statuses for which a call must never be (re-)offered as an incoming ring:
// the terminal set plus `voice_mailing` (the caller is in the voicemail flow).
// Consumed by the realtime offer gates and the boot-hydrate skip.
export const NON_OFFERABLE_STATUSES = [...TERMINAL_STATUSES, 'voice_mailing'];

export const isInbound = direction => direction === 'inbound';

// Whether an error is one of the two actionable microphone-permission failures
// (permission denied / no capture device). Centralized so every join path
// classifies mic errors identically (see ensureMicrophoneAccess for the codes).
export const isMicPermissionError = error =>
  error?.code === 'MIC_BLOCKED' || error?.code === 'MIC_NOT_FOUND';

// Frozen registry of call-error codes used as dynamic i18n keys under
// CONVERSATION.VOICE_CALL.*. Centralized so a typo can't silently render a raw key.
// ANSWERED_BY is the only entry whose string interpolates: it carries a `name` (the
// claimant), so every renderer must pass params through - see FloatingCallWidget.
export const CALL_ERROR_CODES = Object.freeze({
  MIC_BLOCKED: 'MIC_BLOCKED',
  MIC_NOT_FOUND: 'MIC_NOT_FOUND',
  JOIN_FAILED: 'JOIN_FAILED',
  NO_AUDIO_OUTPUT: 'NO_AUDIO_OUTPUT',
  ANSWERED_BY: 'ANSWERED_BY',
});

// Transient MID-CALL media problems, surfaced to the agent as a self-dismissing toast
// rather than as a callError: they are not join failures, they come and go while the leg
// stays up, and the SDK tells us when each one clears. Used as dynamic i18n keys under
// CONVERSATION.VOICE_CALL.*, same convention as CALL_ERROR_CODES.
export const CALL_WARNING_CODES = Object.freeze({
  // Our audio is not reaching Twilio, so the person on the phone hears silence.
  CONTACT_CANNOT_HEAR: 'CONTACT_CANNOT_HEAR',
  // Twilio's audio is not reaching us, so the agent hears silence.
  AGENT_CANNOT_HEAR: 'AGENT_CANNOT_HEAR',
  // The media path itself dropped - usually nobody hears anybody.
  CONNECTION_LOST: 'CONNECTION_LOST',
  // The mic level stopped varying. OBSERVATIONAL on purpose: it is the one warning that can
  // fire while nothing is wrong, so its wording must stay true either way. See the
  // amplitude-vs-packets note on SDK_WARNING_TO_CALL_WARNING.
  MIC_NO_INPUT: 'MIC_NO_INPUT',
});

// @twilio/voice-sdk 2.12.4 warning names -> the agent-facing warning above.
//
// The names are built by Call#_reemitWarning as WARNING_PREFIXES[threshold] + WARNING_NAMES[stat]
// (call.js), so the emitted string carries the prefix: the stat `bytesSent` breaching its `min`
// threshold arrives as 'low-bytes-sent', NOT 'bytes-sent'. Mapping the un-prefixed name would
// build a listener that never fires.
//
// Deliberately NOT mapped, because they would toast during ordinary network variance and train
// agents to ignore the alert: 'high-jitter', 'high-rtt', 'low-mos', 'high-packet-loss',
// 'high-packets-lost-fraction' (quality breadcrumbs - logged only), and 'ice-candidate'
// (negotiation detail, emitted on normal setup). 'constant-audio-output-level' is unmappable by
// construction: Call#_emitWarning explicitly never emits it.
//
// AMPLITUDE vs PACKETS - the reason 'constant-audio-input-level' gets its own code instead of
// being folded into CONTACT_CANNOT_HEAR. They detect different failures with very different
// reliability:
//
//   low-bytes-sent            no PACKETS are leaving. The SDK negotiates PCMU first
//                             (Device codecPreferences [PCMU, Opus]) with DTX nowhere in the
//                             build, and PCMU is constant-bitrate with no silence suppression -
//                             audio keeps flowing while nobody speaks. Zero bytes for 3 seconds
//                             therefore means the transport genuinely stopped. Reliable, so its
//                             copy states the consequence outright.
//
//   constant-audio-input-level  packets are flowing but carry nothing: a dead, muted or wrong
//                             capture device. It is a standard-deviation check on mic amplitude
//                             ({ minStandardDeviation: 327.67, sampleCount: 10 }), so ~10s of a
//                             quiet room with a perfectly good headset also trips it. This file
//                             ALREADY removed amplitude-based detection once for exactly that
//                             reason (see the note in wireCallDiagnostics), and Twilio downgrades
//                             the sibling 'constant-audio-output-level' to info with the comment
//                             "Avoid throwing false positives as warnings until we refactor
//                             volume metrics". Same metric family, so it is kept - a dead mic is
//                             invisible to low-bytes-sent, PCMU sends silence just as happily -
//                             but under OBSERVATIONAL wording that is still true when it cries
//                             wolf.
//
// Note for whoever adds a mute button: the SDK suppresses constant-audio-input-level while
// Call#isMuted(), but low-bytes-sent has NO such guard, so CONTACT_CANNOT_HEAR would need gating
// on mute state or muting yourself would announce that nobody can hear you.
export const SDK_WARNING_TO_CALL_WARNING = Object.freeze({
  'low-bytes-sent': CALL_WARNING_CODES.CONTACT_CANNOT_HEAR,
  'constant-audio-input-level': CALL_WARNING_CODES.MIC_NO_INPUT,
  'low-bytes-received': CALL_WARNING_CODES.AGENT_CANNOT_HEAR,
  'ice-connectivity-lost': CALL_WARNING_CODES.CONNECTION_LOST,
});

const ACCOUNT_PATH_PATTERN = /\/app\/accounts\/(\d+)/;

// The account id from the current /app/accounts/:id/... URL. Shared by the
// store (refuse to hydrate a call persisted under a different account) and the
// boot-hydrate adapter (stamp the account on a recovered offer). Single source
// of truth so the two never drift.
export const currentAccountIdFromUrl = () => {
  try {
    if (typeof window === 'undefined') return null;
    const match = window.location?.pathname?.match(ACCOUNT_PATH_PATTERN);
    return match ? Number(match[1]) : null;
  } catch (error) {
    return null;
  }
};

// Prompt for and confirm microphone access before a join. We grab the stream only to
// trigger the browser permission prompt / surface a hardware failure, then release it
// immediately (Twilio's Call opens its own). The two actionable failures map to
// call-error codes the UI can explain; everything else is treated as blocked.
export const ensureMicrophoneAccess = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
    return true;
  } catch (error) {
    const isMissingDevice =
      error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError';
    const code = isMissingDevice ? 'MIC_NOT_FOUND' : 'MIC_BLOCKED';
    const micError = new Error(code);
    micError.code = code;
    micError.cause = error;
    throw micError;
  }
};
